import { useMenuIconicContentOffset } from 'components/Layout/Header/HeaderBottom/MenuIconic/useMenuIconicContentOffset';
import UserMenuDesktop from 'components/Layout/Header/HeaderBottom/UserMenu/UserMenuDesktop';
import UserMenuMobile from 'components/Layout/Header/HeaderBottom/UserMenu/UserMenuMobile';
import { useCurrentCustomerData } from 'connectors/customer/CurrentCustomer';
import { useSettings } from 'connectors/Settings/Settings';
import { useDomainConfig } from 'context/DomainConfigProvider/DomainConfigProvider';
import { isB2bDomain } from 'helpers/isDomainLocale';
import useTranslation from 'next-translate/useTranslation';
import { RefObject, useMemo, useRef } from 'react';
import { CustomerPageType } from 'types/customer';
import { getInternationalizedStaticUrls } from 'utils/getInternationalizedStaticUrls';

type Props = {
    isMobileView: boolean | undefined;
    userMenuLinkRef: RefObject<HTMLDivElement>;
    isActive: boolean;
};

export type MenuItemProps = {
    link: string | null;
    title: string;
    count: number | null;
    show: boolean;
};

const UserMenu: FC<Props> = ({ isMobileView, userMenuLinkRef, isActive }) => {
    const { t } = useTranslation();

    const customerUserData = useCurrentCustomerData();
    const settings = useSettings();
    const domainConfig = useDomainConfig();

    const userMenuPopupRef = useRef<HTMLDivElement>(null);
    const userMenuDesktopRef = useRef<HTMLUListElement>(null);
    useMenuIconicContentOffset(userMenuLinkRef, userMenuPopupRef);

    const [
        customerProfileUrl,
        customerEditProfileUrl,
        customerOrdersUrl,
        customerMyPointsUrl,
        customerWatchedItemsUrl,
        b2bCustomerOrders,
        b2bCustomerDocuments,
        claimsAndReturnsUrl,
    ] = getInternationalizedStaticUrls(
        [
            '/customer',
            '/customer/edit-profile',
            '/customer/orders',
            '/customer/my-points',
            '/customer/watched-items',
            '/customer/b2b-orders',
            '/customer/b2b-documents',
            '/customer/claims-and-returns',
        ],
        domainConfig.url,
    );

    const menuItems: MenuItemProps[] = useMemo(() => {
        return [
            {
                link: b2bCustomerOrders,
                pageType: CustomerPageType.b2bOrders,
                title: t('Orders'),
                count: null,
                show: isB2bDomain(domainConfig),
            },
            {
                link: b2bCustomerDocuments,
                pageType: CustomerPageType.b2bDocuments,
                title: t('Documents'),
                count: null,
                show: isB2bDomain(domainConfig) && customerUserData?.b2bPermissions.orderDocumentsPage === true,
            },
            {
                link: customerProfileUrl,
                title: t('My information'),
                count: null,
                show: !isB2bDomain(domainConfig),
            },
            {
                link: customerEditProfileUrl,
                title: !isB2bDomain(domainConfig) ? t('My profile') : t('Edit customer data'),
                count: null,
                show: true,
            },
            {
                link: customerOrdersUrl,
                title: t('My purchases'),
                count: null,
                show: !isB2bDomain(domainConfig),
            },
            {
                link: customerMyPointsUrl,
                title: t('My points'),
                count: null,
                show: !isB2bDomain(domainConfig) && settings?.loyaltyClub.enabled === true,
            },
            {
                link: customerWatchedItemsUrl,
                title: t('Watched items'),
                count: customerUserData?.pillCounts.watchDogItems ?? null,
                show: true,
            },
            {
                link: claimsAndReturnsUrl,
                title: t('Claims/Returns'),
                count: customerUserData?.pillCounts.claimsAndReturnsItems ?? 0,
                show: !!settings?.claimsAndReturns.claimsAllowed || !!settings?.claimsAndReturns.returnsAllowed,
            },
        ];
    }, [
        customerEditProfileUrl,
        customerMyPointsUrl,
        customerOrdersUrl,
        customerProfileUrl,
        customerUserData?.pillCounts.watchDogItems,
        customerUserData?.pillCounts.claimsAndReturnsItems,
        customerWatchedItemsUrl,
        b2bCustomerOrders,
        b2bCustomerDocuments,
        domainConfig,
        settings,
        t,
        customerUserData?.b2bPermissions.orderDocumentsPage,
        settings?.claimsAndReturns.claimsAllowed,
        settings?.claimsAndReturns.returnsAllowed,
    ]);

    return isMobileView ? (
        <UserMenuMobile menuItems={menuItems} ref={userMenuPopupRef} />
    ) : (
        <UserMenuDesktop isActive={isActive} menuItems={menuItems} ref={userMenuDesktopRef} />
    );
};

export default UserMenu;
